import './App.css';
import { AppBar, Button, Card, CardHeader, createTheme, CssBaseline, IconButton, Stepper, ThemeProvider, Toolbar, Typography } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { XbattleGame } from 'xbattle-wasm';
import React from 'react';
import { Container } from '@mui/system';

const theme = createTheme({
  typography: {
    fontFamily: [
      'Anonymous Pro',
      'monospace'
    ].join(','),
    // fontSize: 12
  }
});

const steps = [
  {title:'Introduction',page:<Introduction/>},
  {title:'Attack strategies',page:<AttackStrategies/>},
  {title:'Resupply',page:<Resupply/>},
  {title:'Decay',page:<Decay/>},
  {title:'Farms',page:<Farms/>},
  {title:'Terrain',page:<Terrain/>},
  {title:'Visibility',page:<Visibility/>},
  {title:'Tips for beginners',page:<Tips/>},
  {title:'Ranged commands',page:<Ranged/>},
  {title:'Construction',page:<Construction/>},
  {title:'Terraforming',page:<Terraforming/>},
  {title:'Managed Operations',page:<Managed/>},
  {title:'Controls',page:<Controls/>},
  {title:'Options',page:<Options/>},
];

function Introduction() {
  return <>
    <p>The square in the  middle  represents an army  of  black troops,
      which you can command by clicking with the mouse.</p>
    <XbattleGame options="-black me -board 5 -armies 1" />
    <p>The direction
      in which the  troops march  depends on where  in the game square
      you click the  mouse - each game square  is divided into regions
      like this...</p>
    <div className='march-grid'>
      <div><div>up &amp; left</div></div><div><div>march up</div></div><div><div className='right'>up &amp; right</div></div>
      <div><div>march left</div></div><div><div className='centre'>stop</div></div><div><div className='right'>march right</div></div>
      <div><div className='bottom'>down &amp; left</div></div><div><div className='bottom'>march down</div></div><div><div className='right bottom'>down &amp; right</div></div>
    </div>
    <p>
      So,  for instance, to march  to the right,   you click near  the
      right edge of the game square, and you will see a command vector
      appear, pointing right.   Your  troop  square  will now  rapidly
      shrink, and  the neighboring   square will  grow, as  the troops
      march from one square to the next.  If you click near the corner
      of  the game  square  the troops  will split   and  move in both
      directions  at the same   time.   March  your troops  around the
      board to get the feel of the commands.

      The first mouse  button toggles the command  vectors on or  off,
      while  the  second mouse  button clears all command vectors  and
      instates the new one.  Try out both these command modes.
    </p>
    <p>
      There is a text line under the game board. Commands issued while
      the mouse is in the text area will be interpreted differently to
      commands issued on the  game  board.
    </p>
  </>
}

function Controls() {
  return <>
    <Typography variant='h6'>Keyboard Controls</Typography>
    <div className='control-grid'>
      <div>Key</div><div>Action<br/>&amp;<br/>Relevant Options</div><div>Description</div>
      <div>a</div><div>attack<br/>&#8209;attack</div><div className='right'>Point at an enemy cell and press 'a'. All of your cells that border this cell will attack simultaneously.</div>
      <div>c</div><div>cancel<br/>&#8209;manage</div><div className='right'>Cancel any managed operation in progress.</div>
      <div>p,P</div><div>paratroops<br/>&#8209;para,&#8209;manage</div><div className='right'>Transport troops to a remote cell. Point at the source cell and indicate range and direction of the paratroops by the position of the pointer around cell centre.</div>
      <div>g,G</div><div>guns/artillery<br/>&#8209;guns,&#8209;manage</div><div className='right'>Attack a remote cell. Point at the source cell and indicate range and direction of the guns by the position of the pointer around cell centre.</div>
      <div>d,D</div><div>dig<br/>&#8209;dig,&#8209;manage,<br/>&#8209;hills,&#8209;sea</div><div className='right'>Lower the terrain. 'd' will lower by one level, whereas 'D' manages digging until the lowest level is reached.</div>
      <div>f,F</div><div>fill<br/>&#8209;fill,&#8209;manage,<br/>&#8209;hills,&#8209;sea</div><div className='right'>Raise the terrain. 'f' will raise by one level, whereas 'D' manages filling until the highest level is reached.</div>
      <div>b,B</div><div>build<br/>&#8209;build,&#8209;manage</div><div className='right'>Build a base. 'b' builds a section of a base, whereas 'B' manages the building of a complete base.</div>
      <div>s</div><div>scuttle<br/>&#8209;scuttle</div><div className='right'>Scuttle (destroy) a section of a base.</div>
      <div className='bottom'>z</div><div className='bottom'>zero</div><div className='right bottom'>Point at one of your cells and press 'z'. All movement and attack vectors will be cancelled.</div>
    </div>
    <p/>
    <Typography variant='h6'>Pointer Controls</Typography>
    <div className='control-grid'>
      <div>Button</div><div>Relevant Options</div><div>Description</div>
      <div>1st / left</div><div></div><div className='right'>toggle direction command</div>
      <div>2nd / middle</div><div></div><div className='right'>clear any existing direction commands and set new one</div>
      <div>3rd / right</div><div>&#8209;repeat</div><div className='right'>repeat previous direction command</div>
      <div>SHIFT<br/>+<br/>1st / left</div><div>&#8209;march</div><div className='right'>march</div>
      <div>SHIFT<br/>+<br/>2nd / middle</div><div>&#8209;march</div><div className='right'>force march</div>
      <div>SHIFT<br/>+<br/>3rd / right</div><div>&#8209;para</div><div className='right'>paratroops</div>
    </div>
  </>
}

function Options() {
  return <>
    <div className='option-grid'>
      <div>Option</div><div>Arguments</div><div>Description</div>
      <div>&#8209;area</div><div></div><div className='right'>The number of troops in a cell are represented by the area of the troop shape rather than the size.</div>
      <div>&#8209;attack</div><div></div><div className='right'>Enable use of the attack key.</div>
      <div>&#8209;armies</div><div>&lt;number&gt;</div><div className='right'>Initialise the game board with this number of regularly distributed cells filled with troops. See &#8209;militia.</div>
      <div>&#8209;bases</div><div>&lt;number&gt;</div><div className='right'>Initialise the game board with this number of regularly distributed bases. See &#8209;rbases.</div>
      <div>&#8209;board (x=y),<br/>or<br/>&#8209;boardx,&#8209;boardy</div><div>&lt;number&gt;</div><div className='right'>Initialise the game board with these dimensions.</div>
      <div>&#8209;build</div><div>&lt;sections&gt;<br/>&lt;cost&gt;<br/>&lt;limit&gt;</div><div className='right'>Enable building of bases. Each base will be built in the specified number of sections, at the specified cost. The number of bases cannot exceed the specified limit. See &#8209;scuttle</div>
      <div>&#8209;decay</div><div>&lt;rate&gt;</div><div className='right'>Make troops slowly die off at the specified rate. See &#8209;farms.</div>
      <div>&#8209;dig</div><div>&lt;cost&gt;</div><div className='right'>Enable terrain lowering. Each dig will be at the specified cost. See &#8209;fill.</div>
      <div>&#8209;digin</div><div>&lt;level&gt;</div><div className='right'>Provide entrenchment.</div>
      <div>&#8209;disrupt</div><div></div><div className='right'>Attacks break enemy supply lines.</div>
      <div>&#8209;erode</div><div>&lt;threshold&gt;</div><div className='right'>Make unused paths erode at the specified threshold.</div>
      <div>&#8209;farms</div><div>&lt;rate&gt;</div><div className='right'>Troops slowly grow at the specified rate. See &#8209;decay.</div>
      <div>&#8209;fight</div><div>&lt;intensity&gt;</div><div className='right'>Set the intensity of fighting.</div>
      <div>&#8209;fill</div><div>&lt;cost&gt;</div><div className='right'>Enable terrain raising. Each fill will be at the specified cost. See &#8209;dig.</div>
      <div>&#8209;forest</div><div>&lt;density&gt;<br/>&lt;levels&gt;</div><div className='right'>Initialise the game board with this density and number of levels of forests.</div>
      <div>&#8209;guns</div><div>&lt;range&gt;<br/>&lt;cost&gt;<br/>&lt;damage&gt;</div><div className='right'>Enable artillery. Artillery will have the specified maximum range and each shot will have the specified cost and damage.</div>
      <div>&#8209;hex</div><div></div><div className='right'>Initialise the game board using hexagonal cells instead of the default square.</div>
      <div>&#8209;hidden</div><div></div><div className='right'>Enemy direction commands are hidden.</div>
      <div>&#8209;hills</div><div>&lt;slope&gt;<br/>&lt;levels&gt;</div><div className='right'>Initialise the game board with hills having this slope and number of levels.</div>
      <div>&#8209;horizon</div><div>&lt;distance&gt;</div><div className='right'>Enable visibilty restriction where the enemy is not visible beyond the specified distance from occupied cells. See &#8209;localmap.</div>
      <div>&#8209;localmap</div><div>&lt;distance&gt;</div><div className='right'>Enable 'Fog Of War' where cells are not visible beyond the specified distance from occupied cells. See &#8209;horizon.</div>
      <div>&#8209;manage</div><div></div><div className='right'>Enabled managed operations.</div>
      <div>&#8209;march</div><div>&lt;delay&gt;</div><div className='right'>Enabled marching with the specified delay between each march.</div>
      <div>&#8209;maxval</div><div>&lt;value&gt;</div><div className='right'>Initialise game board with cells having this maximum cell troop capacity.</div>
      <div>&#8209;militia</div><div>&lt;number&gt;</div><div className='right'>Initialise the game board with this number of randomly distributed cells filled with troops. See &#8209;armies.</div>
      <div>&#8209;move</div><div>&lt;speed&gt;</div><div className='right'>Speed of troop flow.</div>
      <div>&#8209;nospigot</div><div>&lt;threshold&gt;</div><div className='right'>Cease attack if the number of troops in cell drops below this threshold.</div>
      <div>&#8209;para</div><div>&lt;range&gt;<br/>&lt;cost&gt;<br/>&lt;damage&gt;</div><div className='right'>Enable paratroops. Paratroops will have the specified maximum range and each wave will have the specified cost and invading damage.</div>
      <div>&#8209;peaks</div><div>&lt;number&gt;<br/>&lt;peak bias&gt;<br/>&lt;trough bias&gt;</div><div className='right'>Modify the terraforming algorithm for hills, forest and sea.</div>
      <div>&#8209;rbases</div><div>&lt;number&gt;<br/>&lt;range&gt;</div><div className='right'>Initialise the game board with this number of randomly distributed bases that are at least the specified range from enemies. See &#8209;bases.</div>
      <div>&#8209;repeat</div><div></div><div className='right'>Enable the operation to repeat last direction command.</div>
      <div>&#8209;reserve</div><div></div><div className='right'>Allow reserve of troops.</div>
      <div>&#8209;scuttle</div><div>&lt;cost&gt;</div><div className='right'>Enable scuttling of bases. Destruction of each section of a base will have the specified cost. See &#8209;build.</div>
      <div>&#8209;sea</div><div>&lt;pervasiveness&gt;<br/>&lt;levels&gt;</div><div className='right'>Initialise the game board with sea having this pervasiveness and number of levels.</div>
      <div>&#8209;seed</div><div>&lt;seed&gt;</div><div className='right'>Initialise the game board seeding the randomness generator with this value.</div>
      <div>&#8209;towns</div><div>&lt;number&gt;</div><div className='right'>Initialise the game board with this number of randomly distributed neutral towns.</div>
      <div className='bottom'>&#8209;wrap</div><div className='bottom'></div><div className='bottom right'>Initialise the game board that wraps around the edges.</div>
    </div>
    <p/>
  </>
}

function AttackStrategies() {
  return <>
    <p>Now we will start a  game with two  opposing armies,  black  and
      white, and you can practice attacking the enemy.</p>
    <XbattleGame options="-black me -white :1 -board 5 -armies 3" containerClass='xbattle-game-container' />
    <p>
      The option 'armies 3' creates three armies for each team and arranges
      them in opposing ranks. There are two game windows this time, black
      and white, placed next to each other so that you can see them both.
      You will  only be
      able to command the black  troops in the  left window, and  the
      white troops from the right  window,  although both windows will
      show the same game board.</p>
    <p>
      You can see the two  battle lines drawn  up opposite each other.
      Command one of the  black squares (left window) to  march into a white square.
      You will see that with  equal  initial strength, the attacker is
      under  a disadvantage and  suffers  much more loss, although the
      white might suffer some loss from the attack.
    </p>
    <p>
      Now try something  else - in the white (right) window, command the white
      troops  to  scatter all over   the  game board, splitting   into
      smaller sections by clicking  near the  corners.   Then form the
      remaining  black squares into a column,  and command the head of
      the column to attack the smallest white squares one by one.  You
      will see that  this has a  much greater effect.  During  battle,
      both sides can suffer damage,  but when the  opposing forces are
      unequal, the smaller force suffers  much greater  losses, so you
      should always   strive for local  superiority   to minimize your
      losses.
    </p>
    <p>
      Try arranging flanking attacks by surrounding white squares from
      two or more sides, and  then attacking simultaneously.  You must
      click quickly to   assure  a  simultaneous attack in    order to
      achieve maximum effect.  Here is another rule of xbattle - that a
      simultaneous assault from two  or more directions  increases the
      effectiveness of the attack.
    </p>
    <p>
      Notice that during an attack,  both colors are displayed  in the
      game square in their proper proportions, with an X battle symbol
      as long as the battle continues.
    </p>
    <p>
      Notice that the text  area now contains two  lines, one for each
      team.  Position your mouse in the text area and type some keys -
      you will see the characters appearing on both windows.  When the
      opposing teams are  run from remote sites, the  text line can be
      used to communicate between the windows.
    </p>
  </>
}

function Resupply() {
  return <>
    <p>
      We will now experiment with resupply - the 'towns' option  provides  for randomly positioned
      towns (the circles) with  randomly varying sizes.    These circles
      represent supply sources, and if you  capture one, it becomes an
      endless supply of  additional troops   of your color.  The  game
      board can soon be swamped by the excess troops if you  move them
      around.
    </p>
    <XbattleGame options="-red me -board 6 -armies 1 -towns 5" />
  </>
}

function Decay() {
  return <>
    <p>
      We will now add  a certain decay to the troops,
      which means that the troops will consume their  provisions  at a
      rate proportional to  the number of  troops.  That  means that a
      single supply base can only  support a certain number of troops,
      so that the  number of troops  will only  grow until it  reaches
      that size.  Try to generate as many  troops as you  can from the
      single supply base.
    </p>
    <XbattleGame options="-blue me -board 6 -rbases 1 -decay 10 -speed 10" />
    <p>
      In this game we use the 'rbases' option which will create 1
      full sized base for each team and position them randomly  on the
      game  board.
    </p>
    <p>
      Notice that the shorter the supply line, the larger  the size of
      the army that can be maintained.   This is because of the losses
      along the supply line.  Also,  troops  cut  off from the  supply
      base will  wither   away   and  die.  In  this  example  we also
      increased the speed of  the simulation with  the 'speed' option to
      10, which  is  normally set to  the default of 5.
    </p>
  </>
}

function Farms() {
  return <>
    <p>
      In this example we will use another form of resupply, called
      farms.  Here, every square on the gameboard produces troops at a
      steady rate, so that the
      larger  the area that you occupy,  the larger the  army that you
      can sustain.
    </p>
    <XbattleGame options="-light me -board 6 -farms 9 -militia 9 -decay 5 -speed 10 -repeat" />
    <p>
      and in this case we make use  of another option, called 'militia',
      which creates  troops of random size in  random locations on the
      game board.  Again, try to  create  the largest  army you can by
      funneling the   production  of   all  the farms  into   a single
      location.  This simulates  the large  scale dynamics of   troops
      living  off  the fat  of    the land.  Also,   in  this game, we
      demonstrate the 'repeat' option - if you corner-click one  army to
      march north-east,  for  example,  then  rapidly click  in  other
      squares  using the  RIGHT mouse   button, the north-east command
      will be repeated in these squares.  For multiple repeated clicks
      the 'repeat' option can save you a lot of time aiming  the mouse.
      Note also that we use the color 'light' which  means light gray.
      There are four monochrome colors, black, white, light and dark.
    </p>
  </>
}

function Terrain() {
  return <>
    <p>
      In the next examples, we will demonstrate three types of terrain
      that can be used with xbattle:  seas,  hills, and forests.  Seas
      are simply regions that cannot be traversed by  land troops, and
      thus can  be  used  for  defensive strategy.   The  command line
      option &#8209;seas: 5 will create random  seas with  a density of 5.
      We will also demonstrate the 'hex' option, which creates a board
      of hexagons rather than squares,  but the commands work  in  the
      same way.
    </p>
    <XbattleGame options="-black me -board 10 -cell 32 -farms 5 -militia 5 -decay 5 -sea 5 -hex" />
    <p>
      Hills are represented  as various shades  of green.  Progress  up
      hills is much slower than on the level, but faster again on the
      way  down.  The  steepness of   the hills  is  controled by the
      argument.
    </p>
    <XbattleGame options="-black me -board 10 -cell 32 -farms 5 -militia 5 -decay 5 -hills 9 -hex" />
  </>
}

function Visibility() {
  return <>
    <p>
      Now,  we   will  demonstrate the &#8209;horizon  option  whereby enemy
      activity is only seen when they are within a specified number of
      game squares  from  the  nearest friendly  force,   the argument
      determines  the  range of visibility.   Send  out scouting
      parties from   the magenta   bases to search  out the  four
      hidden  cyan bases.
    </p>
    <XbattleGame options="-magenta me -cyan you -board 10 -cell 32 -rbases 4 -horizon -hex" />
    <p>
      The &#8209;localmap option is a variation of &#8209;horizon where not only
      are the enemy troops hidden but so is the terrain. As with &#8209;horizon, if you advance towards the enemy and then retreat, the fog will conceal the enemy once more.
    </p>
    <XbattleGame options="-magenta me -cyan you -board 10 -cell 32 -rbases 4 -localmap -hex -hills 5 -sea 4 -decay 5" />
  </>
}

function Tips() {
  return <>
    <p>
      Play a few games with some real opponents before going on to the
      next tutorial.  If playing against more experienced players, set
      the  &#8209;speed option  very  low, like  1, or even  fractional
      values like 0.5, to generate  a game  of strategy and intellect,
      where the  manual skill of  rapid  clicking is  not the dominant
      factor in the game.  In the next tutorial  you will learn how to
      bias games, setting  game parameters differently for  individual
      players.
    </p>
  </>
}

function Ranged() {
  return <>
  <p>
  First, we  will   introduce  guns  and   paratroops.  These  are
particularly  handy when you have  seas  that cannot be crossed,
and can  also be used  for commando attacks or to  break  supply
lines behind enemy lines.  Guns are aimed with the  mouse in the
game square from which you wish to shoot, and the  direction and
distance of your cursor click from the center of the game square
determines the azimuth and range of  the shot.  For  example, if
the mouse is in position A below,  i.e. just above  center, then
the shot will  fall  at short range to  the  north.   Position B
represents a long range shot to the north, and C, a medium range
shot to the south  east.  The shot is  triggered by striking the
'g'  key on the  keyboard (g for  'guns') while aiming  with the
mouse.
  </p>
  <p>
  Paratroops  are  launched in exactly  the same way,  except this
time you use  the 'p' key  (p for paratroops).  Every shot costs
one troop, and will kill  one troop in  the enemy square,  while
every paratroop  costs  3  troops from  the  source  square, and
arrives in the enemy square as a normal troop.  Be  careful with
the  guns, because they  can kill  your troops  as  well  as the
enemy's.   Guns  and  paratroops  are  called   with the command
options '-guns' and '-para', each with an argument that controls
the maximum range (in game squares).  For  example, try  out the
following game."                                                
  </p>
  <XbattleGame options="-black me -white you -board 10 -cell 32 -militia 5 -rbases 1 -guns 5 -para 5" />
  </>
}

function Construction() {
  return <>
  <p>
  Another set of options allow you to build and destroy the supply
bases.  The option '&#8209;build &lt;n&gt;' lets you  build them by pointing
to a game square which is completely full of troops and pressing
the 'b' key (b for build).  This operation will cost you all the
troops in  the square, and  will only build  a partial base - the
operation will have to be repeated a number of times  before the
base is complete and can go into production.   The total cost of
building a base is &lt;n&gt; full troop squares.
  </p>
  <p>
  Bases can be scuttled (destroyed) with the 'scuttle' option by
    again positioning   the  cursor on the    base and
pressing the 's' key  (s for  scuttle).  Again, you will have to
have some troops in the square,  and it may take several scuttle
operations  to completely  destroy it, but  each operation costs
only a single  troop,  and  is thus  much cheaper  than building
them.  A base can be scuttled to  prevent it falling  into enemy
hands.
  </p>
  <p>
  Try building and scuttling a few bases in the next example.  Try
doing a partial scuttle and then rebuilding again.
  </p>
  <XbattleGame options="-black me -white you -board 10 -cell 32 -militia 5 -rbases 1 -build 4 -scuttle" />
  </>
}

function Terraforming() {
  return <>
  <p>
  Another way you can modify the game board during the  game is to
fill or  dig, to raise  or lower the  elevation.  When used with
'sea', digging  creates a new square  of sea, while filling can
fill one in.  Each operation costs  &lt;n&gt;  full troop squares, and
is performed by putting  the cursor in  that square  and hitting
'd' to dig.  To fill  in a sea, the troops  cannot march into it
first,  so you must  do the next  best  thing - march your troops
next to  the sea  and then command  them to  march into  it   by
clicking a command vector to point to the sea,  then hit the 'f'
key to fill  in.
  </p>
  <p>
  Try digging and filling a few seas in the next example.
  </p>
  <XbattleGame options="-black me -white you -board 10 -cell 32 -militia 5 -rbases 1 -sea 2 -dig 4 -fill 4" />
  <p>
  When used with the 'hills' option, the  digging and filling can
be used to  lower  or raise the   elevation to  create defensive
fortifications.  In this  case, each  dig or fill costs only one
full  troop square regardless of  the  value you  supply for the
arguments &#8209;dig  and   &#8209;fill.   You must supply  dummy  arguments
nevertheless.  Try this out in the next example."               
  </p>
  <XbattleGame options="-black me -white you -board 10 -cell 32 -militia 5 -rbases 1 -hills 9 -dig 5 -fill 5" />
  </>
}

function Managed() {
  return <>
  <p>
  Finally,  there is an  option  called  'manage'  that works in
conjunction with &#8209;guns,  &#8209;para,  &#8209;dig, &#8209;fill, and &#8209;build,  that
allows  you to manage repeated execution  of those commands with
a single command.  For  example,  with the combination  '&#8209;guns'
and '&#8209;manage'  options, you can  initiate  an artillery barrage
by using the 'G' key instead of the 'g'  key to aim  your guns.
The gun will shoot the same way as usual,  except that it will 
continue sending  additional shells to the same  target  as long
as  the  source square is kept   resupplied.  If the supply is
interrupted,  then  the barrage  will be  temporarily suspended,
but will automatically resume when  fresh supplies arrive.  The
other operations  work similarly, using  the upper-case version
of the regular command key.  Managed operation is  indicated by
the letters 'GUN' for gun, 'PAR' for para, 'DIG' for dig, 'FIL'
for fill, and  'BLD' for  build,  which  appear in the  managed
square.  Managed operation can be cancelled" either striking the
'c' key in the managed square, or by  giving" the managed square
a movement command.
  </p>
  <p>
  The following example allows managed guns,  paratroops, digging,
filling, and base building.
  </p>
  <XbattleGame options="-black me -white you -board 10 -cell 32 -militia 5 -rbases 1 -dig 7 -fill 7 -guns 4 -para 4 -build 7 -scuttle -manage" />
  </>
}

function App() {
  const [activeStep, setActiveStep] = React.useState(0);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // const ActivePage = steps[activeStep].page

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppBar position="static">
        <Toolbar>
          <IconButton disabled={true} edge="start" color="inherit" aria-label="menu">
            <MenuIcon />
          </IconButton>
          <Typography variant="h6">
            XBattle tutorial
          </Typography>
        </Toolbar>
      </AppBar>
      <Stepper activeStep={activeStep} />
      <Container maxWidth='md'>
        <Card style={{ textAlign: 'center' }}>
          <CardHeader title={`${activeStep + 1}. ${steps[activeStep].title}`} />
          <Button
            color="inherit"
            disabled={activeStep === 0}
            onClick={handleBack}
            sx={{ mr: 1 }}
          >
            Back
          </Button>
          <Button
            onClick={handleNext}
            disabled={activeStep >= steps.length - 1}
          >
            Next
          </Button>
          {steps[activeStep].page}
          <Button
            color="inherit"
            disabled={activeStep === 0}
            onClick={handleBack}
            sx={{ mr: 1 }}
          >
            Back
          </Button>
          <Button
            onClick={handleNext}
            disabled={activeStep >= steps.length - 1}
          >
            Next
          </Button>
        </Card>
      </Container>
    </ThemeProvider>
  );
}

export default App;
